import React, { useState, useRef, useEffect } from "react";
import { useRestaurantData } from "./hooks/useRestaurantData";
import { useUsage } from "./hooks/useUsage";
import { useAuth } from "./AuthContext";
import { useSearchParams } from "react-router-dom";
import UpgradeModal from "./UpgradeModal";
import Sidebar from "./Sidebar";
import LoadingScreen from "./LoadingScreen";
import "./GenerateRestaurants.css";

const CITY = { city: "New York", country: "United States", state: "NY" };
const MAX_LEADS_PER_FETCH = 100;

const BOROUGHS = [
  { key: "manhattan",     label: "Manhattan"     },
  { key: "brooklyn",      label: "Brooklyn"      },
  { key: "queens",        label: "Queens"        },
  { key: "bronx",         label: "Bronx"         },
  { key: "staten island", label: "Staten Island" },
];

const GRADE_CFG = {
  A: { color: "#22c55e", bg: "rgba(34,197,94,0.1)",  border: "rgba(34,197,94,0.25)"  },
  B: { color: "#f59e0b", bg: "rgba(245,158,11,0.1)", border: "rgba(245,158,11,0.25)" },
  C: { color: "#ef4444", bg: "rgba(239,68,68,0.1)",  border: "rgba(239,68,68,0.25)"  },
};

const GradeBadge = ({ grade }) => {
  if (!grade) return null;
  const cfg = GRADE_CFG[grade];
  if (!cfg) return null;
  return (
    <span className="gr-permit-badge" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
      {grade}
    </span>
  );
};

const ScoreDot = ({ score }) => {
  const color = score >= 15 ? "#22c55e" : score >= 10 ? "#f59e0b" : "#14202E";
  return (
    <span className="gr-score">
      <span className="gr-score-dot" style={{ background: color }} />
      <span className="gr-score-num">{score}</span>
    </span>
  );
};

const Dash = () => <span className="gr-dash">—</span>;

const ArrowUp = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M8 13.5V2.5M2.5 8L8 2.5L13.5 8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronDown = ({ open }) => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none"
    style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none", flexShrink: 0 }}>
    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ─── Hint pill SVG icons ───────────────────────────────────────────────────────

const IconPhone = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M2 2.5A1.5 1.5 0 013.5 1h1a1 1 0 011 .8l.5 2a1 1 0 01-.3 1l-.7.7a8 8 0 003.5 3.5l.7-.7a1 1 0 011-.3l2 .5a1 1 0 01.8 1v1A1.5 1.5 0 0111.5 12 9.5 9.5 0 012 2.5z"
      stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
);

const IconOwnerPhone = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M2 2.5A1.5 1.5 0 013.5 1h1a1 1 0 011 .8l.5 2a1 1 0 01-.3 1l-.7.7a8 8 0 003.5 3.5l.7-.7a1 1 0 011-.3l2 .5a1 1 0 01.8 1v1A1.5 1.5 0 0111.5 12 9.5 9.5 0 012 2.5z"
      stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
    <circle cx="11" cy="3" r="1.8" fill="currentColor" opacity="0.9"/>
  </svg>
);

const IconGrade = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="1" width="12" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M4.5 7.5l2 2 3-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconEmail = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <rect x="1" y="3" width="12" height="8" rx="2" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M1 5l6 4 6-4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
  </svg>
);

const IconWebsite = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M1 7h12M7 1c-2 2-2 8 0 12M7 1c2 2 2 8 0 12" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

const IconAI = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M7 1l1.5 4H13l-3.5 2.5 1.5 4L7 9l-4 2.5 1.5-4L1 5h4.5L7 1z"
      stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
  </svg>
);

const IconCSV = () => (
  <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
    <path d="M3 1h6l4 4v8a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M9 1v4h4M5 8l2 2 2-2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const HINTS = [
  { icon: <IconPhone />,      label: "Business Phone"    },
  { icon: <IconOwnerPhone />, label: "Owner Direct Line" },
  { icon: <IconGrade />,      label: "Restaurant Grade"  },
  { icon: <IconEmail />,      label: "Email"             },
  { icon: <IconWebsite />,    label: "Website"           },
  { icon: <IconAI />,         label: "AI Outreach"       },
  { icon: <IconCSV />,        label: "CSV Export"        },
];

export default function GenerateRestaurants() {
  const { user, showAuthModal } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgradeToast, setUpgradeToast] = useState(false);

  useEffect(() => {
    if (searchParams.get('upgraded') === 'true') {
      setUpgradeToast(true);
      setSearchParams({}, { replace: true });
      setTimeout(() => setUpgradeToast(false), 4000);
    }
  }, []);

  const {
    maxResults, setMaxResults,
    loading, restaurants, setRestaurants,
    error, executionTime, operatorDataAvailable,
    fetchRestaurantData, exportToCSV, copyToClipboard, openInMaps,
    getRestaurantsWithContact,
    getRestaurantsWithOperatorPhone,
    getRestaurantsWithGradeA,
    queryAI, clearAI, loadMoreScripts,
    aiLoading, aiResult, aiError, aiActive,
    aiScripts, aiHasMore, aiTotalFound,
    setAiResult, setAiActive,
  } = useRestaurantData();

  const {
    plan, leadsUsed, leadsLimit, leadsRemaining, isAtLimit,
    seenIds, searches,
    canSearch, recordSearch, recordAIChat, loadSearch, loadAIChat,
    deleteSearch, renameSearch,
  } = useUsage();

  useEffect(() => {
    const planDefault = Math.min(leadsRemaining, MAX_LEADS_PER_FETCH);
    setMaxResults(planDefault > 0 ? planDefault : 10);
  }, [plan]);

  const [sidebarOpen,    setSidebarOpen]    = useState(true);
  const [copiedId,       setCopiedId]       = useState(null);
  const [aiQuery,        setAiQuery]        = useState('');
  const [showUpgrade,    setShowUpgrade]    = useState(false);
  const [activeSearchId, setActiveSearchId] = useState(null);
  const [activeChatId,   setActiveChatId]   = useState(null);

  const [selectedBorough, setSelectedBorough] = useState(null);
  const [boroughOpen,     setBoroughOpen]     = useState(false);
  const boroughRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (boroughRef.current && !boroughRef.current.contains(e.target)) setBoroughOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasResults  = restaurants.length > 0;
  const hasSearched = hasResults || !!error;
  const displayList = aiActive && aiResult?.restaurants?.length ? aiResult.restaurants : restaurants;
  const SW = sidebarOpen ? 240 : 48;

  const handleLeadsChange = (val) => {
    const n = parseInt(val);
    if (isNaN(n)) return;
    setMaxResults(Math.min(n, MAX_LEADS_PER_FETCH));
  };

  const activeBoroughLabel = selectedBorough ? BOROUGHS.find(b => b.key === selectedBorough)?.label : null;

  const doSearch = () => {
    if (loading) return;
    if (!user) { showAuthModal('signup'); return; }
    const check = canSearch(maxResults);
    if (!check.allowed) { setShowUpgrade(true); return; }
    const effectiveMax = Math.min(maxResults, check.available, MAX_LEADS_PER_FETCH);
    if (effectiveMax !== maxResults) setMaxResults(effectiveMax);
    setActiveSearchId(null);
    setActiveChatId(null);
    activeSearchIdRef.current = null;
    fetchRestaurantData(null, CITY.city, CITY.country, seenIds, effectiveMax, selectedBorough);
  };

  const prevLengthRef     = useRef(0);
  const activeSearchIdRef = useRef(null);
  const aiQueryRef        = useRef('');
  const activeChatIdRef   = useRef(null);

  useEffect(() => { activeSearchIdRef.current = activeSearchId; }, [activeSearchId]);
  useEffect(() => { aiQueryRef.current = aiQuery; },             [aiQuery]);
  useEffect(() => { activeChatIdRef.current = activeChatId; },   [activeChatId]);

  useEffect(() => {
    if (restaurants.length === 0 || restaurants.length === prevLengthRef.current) return;
    prevLengthRef.current = restaurants.length;

    recordSearch(restaurants).then(result => {
      if (!result) return;
      activeSearchIdRef.current = result.entry.id;
      setActiveSearchId(result.entry.id);
    });
  }, [restaurants.length]);

  const prevAiResultRef = useRef(null);
  useEffect(() => {
    if (!aiResult)                             return;
    if (aiResult === prevAiResultRef.current)  return;
    if (activeChatIdRef.current)               return;
    if (!activeSearchIdRef.current)            return;

    prevAiResultRef.current = aiResult;
    recordAIChat(activeSearchIdRef.current, aiQueryRef.current, aiResult);
  }, [aiResult]);

  const handleLoadSearch = (searchId) => {
    const entry = loadSearch(searchId);
    if (!entry?.restaurants?.length) return;
    activeSearchIdRef.current = searchId;
    setActiveSearchId(searchId);
    setActiveChatId(null);
    activeChatIdRef.current = null;
    prevLengthRef.current = entry.restaurants.length;
    setRestaurants(entry.restaurants);
    clearAI();
    setAiQuery('');
    aiQueryRef.current = '';
  };

  const handleLoadAIChat = (searchId, chatId) => {
    const data = loadAIChat(searchId, chatId);
    if (!data) return;
    activeSearchIdRef.current = searchId;
    setActiveSearchId(searchId);
    setActiveChatId(chatId);
    activeChatIdRef.current = chatId;
    prevLengthRef.current = data.restaurants.length;
    setRestaurants(data.restaurants);
    setAiResult(data.aiResult);
    setAiActive(true);
    setAiQuery(data.query || '');
    aiQueryRef.current = data.query || '';
  };

  const handleQueryAI = async (query) => {
    if (!query?.trim()) return;
    setActiveChatId(null);
    activeChatIdRef.current = null;
    await queryAI(query);
  };

  const handleNewSearch = () => {
    setRestaurants([]);
    clearAI();
    setAiQuery('');
    aiQueryRef.current = '';
    setActiveSearchId(null);
    setActiveChatId(null);
    activeSearchIdRef.current = null;
    activeChatIdRef.current = null;
    prevLengthRef.current = 0;
  };

  const doCopy = (r) => {
    copyToClipboard(
      [
        r.name, r.address,
        r.phone                       && `Phone: ${r.phone}`,
        r.email                       && `Email: ${r.email}`,
        r.website                     && `Web: ${r.website}`,
        r.operatorData?.operatorPhone && `Owner Phone: ${r.operatorData.operatorPhone}`,
        r.operatorData?.healthGrade   && `Health Grade: ${r.operatorData.healthGrade}`,
        r.operatorData?.dba           && `DBA: ${r.operatorData.dba}`,
      ].filter(Boolean).join("\n"),
      r.name
    );
    setCopiedId(r.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const BoroughSelector = () => (
    <div className="gr-borough-wrap">
      <div className="gr-borough-trigger" onClick={() => setBoroughOpen(v => !v)}>
        <span className="gr-borough-title">New York City</span>
        <ChevronDown open={boroughOpen} />
      </div>
      <div className={`gr-borough-sub${activeBoroughLabel ? ' active' : ''}`}>
        {activeBoroughLabel ? `↳ ${activeBoroughLabel}` : "All boroughs · Updated weekly"}
      </div>
      {boroughOpen && (
        <div className="gr-borough-dropdown">
          <div
            className={`gr-borough-option${!selectedBorough ? ' selected' : ''}`}
            onClick={() => { setSelectedBorough(null); setBoroughOpen(false); }}
          >
            All boroughs {!selectedBorough && <span className="gr-borough-check">✓</span>}
          </div>
          {BOROUGHS.map((b) => (
            <div
              key={b.key}
              className={`gr-borough-option${selectedBorough === b.key ? ' selected' : ''}`}
              onClick={() => { setSelectedBorough(b.key); setBoroughOpen(false); }}
            >
              {b.label} {selectedBorough === b.key && <span className="gr-borough-check">✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="gr-root">
      <Sidebar
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        user={user} plan={plan} leadsUsed={leadsUsed} leadsLimit={leadsLimit}
        isAtLimit={isAtLimit} loading={loading} searches={searches}
        activeSearchId={activeSearchId} activeChatId={activeChatId}
        onLoadSearch={handleLoadSearch} onLoadAIChat={handleLoadAIChat}
        onUpgrade={() => setShowUpgrade(true)}
        onDeleteSearch={deleteSearch} onRenameSearch={renameSearch}
        showAuthModal={showAuthModal}
        onNewSearch={handleNewSearch}
      />

      <div className="gr-main" style={{ marginLeft: SW }}>

        {!hasSearched && (
          <div
            className="gr-hero"
            style={{
              paddingTop: 80,
              opacity: loading ? 0.45 : 1,
              pointerEvents: loading ? 'none' : 'auto',
              transition: 'opacity 0.3s ease',
            }}
          >
            <div className="gr-hero-eyebrow">
              <span className="gr-hero-eyebrow-dot" />
              {plan.charAt(0).toUpperCase() + plan.slice(1)} Plan · {leadsRemaining} leads remaining
            </div>
            <div className="gr-prompt-box gr-prompt-box-hero" style={{ maxWidth: 680, padding: '24px 24px 24px 28px', borderRadius: 24, gap: 18, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 38, flexShrink: 0 }}>🗽</span>
              <div ref={boroughRef} style={{ flex: 1, minWidth: 180 }}>
                <BoroughSelector />
              </div>
              <div className="gr-divider" />
              {user ? (
                <>
                  <div className="gr-leads-wrap" style={{ flexDirection: 'column', alignItems: 'center', gap: 2, padding: '0 8px' }}>
                    <input type="number" className="gr-leads-input gr-leads-input-hero" value={maxResults} min="1" max={MAX_LEADS_PER_FETCH}
                      onChange={e => handleLeadsChange(e.target.value)} onClick={e => e.stopPropagation()} />
                    <span className="gr-leads-hint">leads · tap to edit</span>
                  </div>
                  <button className="gr-send-btn ready" style={{ width: 56, height: 56, borderRadius: 16, flexShrink: 0 }}
                    onClick={doSearch} disabled={loading}>
                    {loading ? <div className="gr-send-spinner" style={{ width: 16, height: 16 }} /> : <ArrowUp size={20} />}
                  </button>
                </>
              ) : (
                <button className="gr-send-btn ready"
                  style={{ width: 'auto', height: 48, borderRadius: 12, padding: '0 22px', fontSize: 13, fontWeight: 600, fontFamily: "'DM Sans', sans-serif", flexShrink: 0 }}
                  onClick={() => showAuthModal('signup')}>Sign in to search →</button>
              )}
            </div>
            {!loading && (
              <>
                <div className="gr-max-leads-note">
                  Max {MAX_LEADS_PER_FETCH} leads per search
                </div>
                <div className="gr-hints" style={{ marginTop: 16 }}>
                  {HINTS.map(h => (
                    <span key={h.label} className="gr-hint-pill" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {h.icon}{h.label}
                    </span>
                  ))}
                </div>
              </>
            )}

            {loading && (
              <LoadingScreen
                selectedBorough={selectedBorough}
                maxResults={maxResults}
              />
            )}
          </div>
        )}

        {error && !loading && <div className="gr-error">⚠ {error}</div>}

        {!loading && hasResults && (
          <div className="gr-results">
            <div className="gr-topbar">
              <div className="gr-prompt-box-compact">
                <img src="/headerlogo.png" alt="" style={{ width: 18, height: 18, borderRadius: 3, flexShrink: 0 }} />
                <div className="gr-topbar-city-info">
                  <span className="gr-topbar-city-name">New York City</span>
                  {activeBoroughLabel && (
                    <span className="gr-topbar-borough">↳ {activeBoroughLabel}</span>
                  )}
                </div>
                <div className="gr-divider" />
                <span className="gr-topbar-leads-count">
                  {restaurants.length} leads generated
                </span>
              </div>
              <div className="gr-topbar-right">
                <div className="gr-topbar-stats">
                  {[
                    { label: "total",   val: restaurants.length,                                       cls: "" },
                    { label: "phone",   val: getRestaurantsWithContact(restaurants, "phone").length,   cls: "accent" },
                    { label: "email",   val: getRestaurantsWithContact(restaurants, "email").length,   cls: "accent" },
                    { label: "web",     val: getRestaurantsWithContact(restaurants, "website").length, cls: "" },
                    ...(operatorDataAvailable ? [
                      { label: "op.phone", val: getRestaurantsWithOperatorPhone(restaurants).length, cls: "green" },
                      { label: "grade A",  val: getRestaurantsWithGradeA(restaurants).length,        cls: "green" },
                    ] : []),
                  ].map(s => (
                    <div key={s.label} className="gr-tstat">
                      <span className="gr-tstat-label">{s.label}</span>
                      <span className={`gr-tstat-val${s.cls ? ` ${s.cls}` : ""}`}>{s.val.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                {executionTime && <span className="gr-exec-time">{(executionTime / 1000).toFixed(1)}s</span>}
                <button className="gr-export-btn" onClick={() => exportToCSV(restaurants, CITY.city)}>Export CSV</button>
              </div>
            </div>

            <div className="gr-body">
              <div className="gr-ai-box">
                <div className="gr-ai-input-row">
                  <div className="gr-ai-icon">✦</div>
                  <input className="gr-ai-input"
                    placeholder={`Ask anything — "Find Italian places with no website" · "Write a cold email for Bâtard, I sell POS systems"`}
                    value={aiQuery}
                    onChange={e => { setAiQuery(e.target.value); aiQueryRef.current = e.target.value; }}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && aiQuery.trim()) { e.preventDefault(); handleQueryAI(aiQuery); } }}
                    disabled={aiLoading}
                  />
                  {aiActive && (
                    <button className="gr-ai-clear-btn" onClick={() => { clearAI(); setAiQuery(''); aiQueryRef.current = ''; setActiveChatId(null); activeChatIdRef.current = null; }}>Clear</button>
                  )}
                  <button className={`gr-ai-send-btn${aiQuery.trim() ? ' ready' : ''}`}
                    disabled={!aiQuery.trim() || aiLoading} onClick={() => handleQueryAI(aiQuery)}>
                    {aiLoading ? <div className="gr-send-spinner" style={{ width: 12, height: 12 }} /> : '↵'}
                  </button>
                </div>

                {aiLoading && (
                  <div className="gr-ai-status">
                    <div className="gr-send-spinner" style={{ width: 10, height: 10 }} />
                    <span>AI is analysing {restaurants.length} restaurants...</span>
                  </div>
                )}
                {aiError && !aiLoading && <div className="gr-ai-error">⚠ {aiError}</div>}
                {aiResult && !aiLoading && (
                  <div className="gr-ai-result-header">
                    <span className={`gr-ai-mode-badge gr-ai-mode-${aiResult.mode}`}>
                      {aiResult.mode === 'filter'  && '🔍 Filter'}
                      {aiResult.mode === 'analyse' && '📊 Analysis'}
                      {aiResult.mode === 'email'   && '✉️ Script'}
                      {aiResult.mode === 'script'  && '✉️ Script'}
                      {aiResult.mode === 'bulk'    && '📋 Bulk Scripts'}
                    </span>
                    <span className="gr-ai-summary">{aiResult.summary}</span>
                    <span className="gr-ai-count">{aiResult.totalMatches} results</span>
                  </div>
                )}

                {aiScripts.length > 0 && !aiLoading && (
                  <div className="gr-ai-scripts">
                    {aiTotalFound > 5 && (
                      <div className="gr-ai-batch-progress">
                        <span className="gr-ai-batch-label">{aiScripts.length} of {aiTotalFound} scripts generated</span>
                        <div className="gr-ai-batch-track">
                          <div className="gr-ai-batch-fill" style={{ width: `${Math.min((aiScripts.length / aiTotalFound) * 100, 100)}%` }} />
                        </div>
                      </div>
                    )}
                    {aiScripts.map((s, i) => (
                      <div key={i} className="gr-ai-script-card">
                        <div className="gr-ai-script-name">{s.restaurantName}</div>
                        {s.subject && <div className="gr-ai-script-row"><span className="gr-ai-script-label">Subject</span><span className="gr-ai-script-val">{s.subject}</span></div>}
                        {s.email && <div className="gr-ai-script-row"><span className="gr-ai-script-label">Email</span><span className="gr-ai-script-val gr-ai-script-body">{s.email}</span></div>}
                        <button className="gr-ai-script-copy"
                          onClick={() => copyToClipboard(`Subject: ${s.subject}\n\n${s.email}`, s.restaurantName)}>
                          Copy All
                        </button>
                      </div>
                    ))}
                    {aiHasMore && (
                      <button className="gr-ai-load-more" onClick={loadMoreScripts} disabled={aiLoading}>
                        {aiLoading
                          ? <><div className="gr-send-spinner" style={{ width: 12, height: 12 }} /> Generating...</>
                          : `Generate Next 5 Scripts (${aiTotalFound - aiScripts.length} remaining)`}
                      </button>
                    )}
                    {!aiHasMore && aiTotalFound > 5 && (
                      <div className="gr-ai-batch-done">✓ All {aiTotalFound} scripts generated</div>
                    )}
                  </div>
                )}
              </div>

              <div className="gr-table-scroll" style={{ '--sw': sidebarOpen ? '240px' : '48px' }}>
                <div className="gr-table-wrap">
                  <div className="gr-thead">
                    {["#", "Restaurant", "Address", "Phone", "Owner Info", "Website", "Email", "Cuisine", "Sc.", "Actions"].map(h => (
                      <div key={h} className="gr-th">{h}</div>
                    ))}
                  </div>
                  {displayList.length === 0 ? (
                    <div className="gr-empty">No results match this filter.</div>
                  ) : (
                    displayList.map((r, i) => (
                      <div key={r.id || i} className="gr-row">
                        <div className="gr-td gr-td-idx">{i + 1}</div>
                        <div className="gr-td gr-td-name">
                          <div className="gr-badge-row">
                            {r.completenessScore >= 15 && <span className="gr-hq-badge">HQ</span>}
                            <GradeBadge grade={r.operatorData?.healthGrade} />
                          </div>
                          <span className="gr-restaurant-name" title={r.name}>{r.name || "Unknown"}</span>
                          {r.aiReason && <span className="gr-ai-reason">✦ {r.aiReason}</span>}
                        </div>
                        <div className="gr-td gr-td-addr"><span className="gr-addr">{r.address || "—"}</span></div>
                        <div className="gr-td">
                          {r.formattedPhone ? <a href={`tel:${r.phone}`} className="gr-phone-link">{r.formattedPhone}</a> : <Dash />}
                        </div>
                        <div className="gr-td gr-td-owner-info">
                          {r.operatorData ? (
                            <div className="gr-owner-info-cell">
                              {r.operatorData.operatorPhone && (
                                <a href={`tel:${r.operatorData.operatorPhone}`} className="gr-owner-phone-number">{r.operatorData.operatorPhone}</a>
                              )}
                              {r.operatorData.healthGrade && (
                                <span className="gr-owner-grade" style={{
                                  color: GRADE_CFG[r.operatorData.healthGrade]?.color || '#14202E',
                                  background: GRADE_CFG[r.operatorData.healthGrade]?.bg || '#F6F7F9',
                                  border: `1px solid ${GRADE_CFG[r.operatorData.healthGrade]?.border || '#CDD3DC'}`,
                                }}>Grade {r.operatorData.healthGrade}</span>
                              )}
                              {r.operatorData.dba && r.operatorData.dba !== r.name && (
                                <span className="gr-owner-dba">↳ {r.operatorData.dba}</span>
                              )}
                            </div>
                          ) : <Dash />}
                        </div>
                        <div className="gr-td gr-td-website">
                          {r.formattedWebsite
                            ? <a href={r.formattedWebsite} target="_blank" rel="noopener noreferrer" className="gr-site-link" title={r.formattedWebsite}>
                                {r.formattedWebsite.replace(/^https?:\/\/(www\.)?/, '')}
                              </a>
                            : <Dash />}
                        </div>
                        <div className="gr-td">
                          {r.isValidEmail ? <a href={`mailto:${r.email}`} className="gr-email-link">{r.email}</a> : <Dash />}
                        </div>
                        <div className="gr-td">
                          <span className="gr-cuisine">{r.operatorData?.healthCuisine || r.cuisine || "—"}</span>
                        </div>
                        <div className="gr-td" style={{ justifyContent: "center" }}>
                          <ScoreDot score={r.completenessScore || 0} />
                        </div>
                        <div className="gr-td gr-td-actions">
                          {r.lat && r.lon && (
                            <button className="gr-act-btn" onClick={() => openInMaps(r.lat, r.lon, r.name)}>Map</button>
                          )}
                          <button className={`gr-act-btn${copiedId === r.id ? " copied" : ""}`} onClick={() => doCopy(r)}>
                            {copiedId === r.id ? "✓" : "Copy"}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {showUpgrade && (
        <UpgradeModal onClose={() => setShowUpgrade(false)} leadsUsed={leadsUsed} leadsLimit={leadsLimit} plan={plan} />
      )}

      {upgradeToast && (
        <div className="gr-toast">
          <span className="gr-toast-check">✓</span>
          <span className="gr-toast-text">
            Plan upgraded — your new leads quota is active
          </span>
        </div>
      )}
    </div>
  );
}